import * as React from 'react';
import type { MapInstance, MapPin } from '@ooh/ui/components/map-view';
import { useSearchParam } from '../../../hooks/use-search-param';
import { parseCoordinates } from '../lib/parse-coordinates';
import type { Coordinates } from '../lib/parse-coordinates';
import { COORDINATE_SEARCH_ZOOM } from './use-map-search';
import { useMapTextSearchEnabled } from './use-map-text-search-enabled';
import { useGeocodingSearch } from './use-geocoding-search';

/** Padding (px) applied around the fitted bounds for a text-search result set. */
const FIT_BOUNDS_PADDING = 64;
/** Zoom cap so a single matching pin does not zoom in to street level. */
const FIT_BOUNDS_MAX_ZOOM = 14;

export interface UseMapUnifiedSearchArgs {
  /** The live MapLibre instance, or null until the map is ready. */
  mapInstance: MapInstance | null;
}

export interface UseMapUnifiedSearchResult {
  /** Current search term from the URL (source of truth, shareable). */
  searchValue: string;
  /** Writes the term back to the URL (debounced by the input component). */
  setSearchValue: (next: string) => void;
  /**
   * The text term forwarded to the pins query; empty unless map-text-search
   * flag is ON and the input is not a coordinate.
   */
  searchFilter: string;
  /** Marker to render for the last coordinate or geocoding hit, or null. */
  markerPosition: Coordinates | null;
  /** Display name from geocoding (e.g. "Braniborska, Wrocław, Polska"), or null. */
  geocodingDisplayName: string | null;
  /** True while a geocoding request is in-flight. */
  isGeocodingLoading: boolean;
  /** True when geocoding failed (location not found). */
  isGeocodingError: boolean;
}

/**
 * Unifies the map search box behavior on the same URL `search` param the catalog
 * uses, so the term is shareable and consistent across both screens.
 *
 * Three paths, evaluated in order:
 *
 * 1. **Coordinate** (decimal or DMS): drop a marker, ease the camera. The term
 *    is NOT forwarded to the pins query. Works regardless of feature flags.
 *
 * 2. **Text + map-text-search ON**: the legacy path. `searchFilter` is the term
 *    forwarded to the pins query (media-unit text search). Camera is fitted to
 *    matching pins by {@link useFitBoundsToPins}.
 *
 * 3. **Text + map-text-search OFF** (default): geocoding path. The term is sent
 *    to `GET /geocoding/search`, the returned POI is used as the marker, and the
 *    camera eases to that point. `searchFilter` stays empty so no media-unit
 *    query is triggered.
 */
export function useMapUnifiedSearch({
  mapInstance,
}: UseMapUnifiedSearchArgs): UseMapUnifiedSearchResult {
  const [searchValue, setSearchValue] = useSearchParam();
  const isTextSearchEnabled = useMapTextSearchEnabled();

  const coordinates = React.useMemo(() => parseCoordinates(searchValue), [searchValue]);

  // Determine which path applies:
  // - coordinates: camera jump (path 1)
  // - text + flag ON: media-unit text search (path 2)
  // - text + flag OFF: geocoding (path 3)
  const isCoordinate = coordinates !== null;
  const trimmedValue = searchValue.trim();
  const isTextInput = !isCoordinate && trimmedValue.length > 0;

  // Path 2: forward text to pins query only when the flag is on.
  const searchFilter = isTextInput && isTextSearchEnabled ? trimmedValue : '';

  // Path 3: geocode when text input and text-search flag is OFF.
  const geocodingQuery = isTextInput && !isTextSearchEnabled ? trimmedValue : '';
  const {
    coordinates: geocodedCoordinates,
    displayName: geocodingDisplayName,
    isLoading: isGeocodingLoading,
    isError: isGeocodingError,
  } = useGeocodingSearch(geocodingQuery);

  // Resolve the marker position: coordinate > geocoded > none.
  const markerPosition = coordinates ?? geocodedCoordinates;

  // Path 1: ease camera to coordinate.
  React.useEffect(() => {
    if (!coordinates || !mapInstance) return;
    mapInstance.easeTo({
      center: [coordinates.lng, coordinates.lat],
      zoom: COORDINATE_SEARCH_ZOOM,
      duration: 600,
    });
  }, [coordinates, mapInstance]);

  // Path 3: ease camera to geocoded POI when result arrives.
  React.useEffect(() => {
    if (!geocodedCoordinates || !mapInstance || isGeocodingLoading) return;
    mapInstance.easeTo({
      center: [geocodedCoordinates.lng, geocodedCoordinates.lat],
      zoom: COORDINATE_SEARCH_ZOOM,
      duration: 600,
    });
  }, [geocodedCoordinates, mapInstance, isGeocodingLoading]);

  return {
    searchValue,
    setSearchValue,
    searchFilter,
    markerPosition,
    geocodingDisplayName: geocodedCoordinates ? geocodingDisplayName : null,
    isGeocodingLoading,
    isGeocodingError,
  };
}

/**
 * Fits the map camera to the matching pins once a text search resolves.
 *
 * Fires when the pins for the active term arrive, but only fits once per term
 * (tracked via a ref) so subsequent pan/zoom pin refreshes do not re-fit and
 * fight the user. Clearing the term resets the guard and never moves the camera,
 * so the camera stays where the user left it.
 */
export function useFitBoundsToPins(
  mapInstance: MapInstance | null,
  searchFilter: string,
  pins: MapPin[],
): void {
  const lastFittedTermRef = React.useRef<string | null>(null);

  React.useEffect(() => {
    if (!searchFilter) {
      // Term cleared: reset so the next identical term fits again.
      lastFittedTermRef.current = null;
      return;
    }
    if (!mapInstance || pins.length === 0) return;
    if (lastFittedTermRef.current === searchFilter) return;

    let minLng = Infinity;
    let minLat = Infinity;
    let maxLng = -Infinity;
    let maxLat = -Infinity;
    for (const pin of pins) {
      if (pin.longitude < minLng) minLng = pin.longitude;
      if (pin.longitude > maxLng) maxLng = pin.longitude;
      if (pin.latitude < minLat) minLat = pin.latitude;
      if (pin.latitude > maxLat) maxLat = pin.latitude;
    }
    mapInstance.fitBounds(
      [
        [minLng, minLat],
        [maxLng, maxLat],
      ],
      { padding: FIT_BOUNDS_PADDING, maxZoom: FIT_BOUNDS_MAX_ZOOM, duration: 600 },
    );
    lastFittedTermRef.current = searchFilter;
  }, [mapInstance, searchFilter, pins]);
}
